import React from "react";
import { useSubmit } from "@remix-run/react";
import { LabeledSelect } from "@core/components/forms/LabeledSelect";
import { PaymentMethodModel } from "@/apidtos";
import { WalletBlock } from "@phoenix/payments/components/WalletBlock";
import { CreateCreditCardModal } from "@phoenix/payments/components/CreateCreditCardModal";
import { CreateACHModal } from "@phoenix/payments/components/CreateACHModal";
import { useDialog } from "@core/components/ui/use-dialog";
import { useAddressBook } from "@core/hooks/useAddressBook";
import { usePaymentsConfig } from "@phoenix/payments/hooks/usePaymentsConfig";
import { useTranslation } from "react-i18next";
import ExtensionPoint from "@core/components/PipelineComponents/ExtensionPoint";
import { useCurrencies } from "@/plugins/products/hooks/useCurrencies";
import { PaymentContactsProvider } from "@phoenix/payments/contexts/PaymentContactsContext";

interface PaymentMethodSelectorProps {
  methods: PaymentMethodModel[];
  onChange?: (id?: number) => void;
  noCreate?: boolean;
}

export const PaymentMethodSelector = ({
  methods,
  onChange,
  noCreate,
}: PaymentMethodSelectorProps) => {
  const primaryPaymentMethod = methods?.find((x) => x.IsPrimary)?.Id;
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = React.useState<
    number | ""
  >(primaryPaymentMethod ?? "");
  const pendingSelectedIdRef = React.useRef<number | null>(null);
  const { dialog, dismiss } = useDialog();
  const submit = useSubmit();
  const addressBook = useAddressBook();
  const settings = usePaymentsConfig();
  const { format: formatAsCurrency } = useCurrencies();
  const { t } = useTranslation();

  React.useEffect(() => {
    if (selectedPaymentMethodId === "") {
      return;
    }
    if (onChange) {
      onChange(selectedPaymentMethodId as number);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPaymentMethodId]);

  // When methods refresh after creating a new card/ACH, preselect the newly created one
  React.useEffect(() => {
    if (
      pendingSelectedIdRef.current != null &&
      methods?.some((m) => m.Id === pendingSelectedIdRef.current)
    ) {
      const id = pendingSelectedIdRef.current;
      pendingSelectedIdRef.current = null;
      setSelectedPaymentMethodId(id);
    }
  }, [methods]);

  const onCreateSuccess = (paymentMethodId?: number) => {
    dismiss();
    if (typeof paymentMethodId === "number") {
      pendingSelectedIdRef.current = paymentMethodId;
    }
    // This calls the loader function again to refresh the payment methods, do not remove
    submit({ method: "GET" }, { preventScrollReset: true });
  };

  const openCreateModal = () => {
    dialog({
      title: t("ui.addCreditCard", "Add Credit Card"),
      content: (
        <PaymentContactsProvider
          contacts={addressBook.data!.map((pm) => pm.Secondary)}
        >
          <CreateCreditCardModal onSuccess={onCreateSuccess} />
        </PaymentContactsProvider>
      ),
      className: "p-4 min-w-[95%] sm:min-w-[50%] xl:min-w-[30%]",
    });
  };

  const openCreateBankModal = () => {
    dialog({
      title: t("ui.addBankAccount", "Add Bank Account"),
      content: (
        <PaymentContactsProvider
          contacts={addressBook.data!.map((pm) => pm.Secondary)}
        >
          <CreateACHModal onSuccess={onCreateSuccess} />
        </PaymentContactsProvider>
      ),
      className: "p-4 min-w-[95%] sm:min-w-[50%] xl:min-w-[30%]",
    });
  };

  const handleChange = (value: string) => {
    if (value === "addCard") {
      openCreateModal();
      return;
    }
    if (value === "addBank") {
      openCreateBankModal();
      return;
    }
    const id = parseInt(value);
    const selected = methods?.find((x) => x.Id === id);
    setSelectedPaymentMethodId(selected?.Id ?? "");
  };

  const selectedPaymentMethod = methods.find(
    (m: PaymentMethodModel) => m.Id === selectedPaymentMethodId
  );

  return (
    <div className="space-y-4">
      <div className="flex items-end">
        <LabeledSelect
          label={t("ui.paymentMethod", "Payment Method")}
          className="flex-grow"
          name="paymentMethodId"
          value={selectedPaymentMethodId}
          onChange={(e) => handleChange(e.target.value)}
        >
          <option disabled value="">
            {t("ui.select", "Select")}
          </option>
          {methods?.map((m) => (
            <option key={m.Id} value={m.Id}>
              {m.Name}{" "}
              {m.TypeId?.toLowerCase() === "credit" && (
                <span>
                  ({t("ui.currentBalance", "Current Balance")}{" "}
                  {formatAsCurrency(Math.abs(m.CurrentBalance ?? 0))})
                </span>
              )}
            </option>
          ))}
          {!noCreate && settings.CreditCardEnabled && (
            <option value="addCard">
              {t("ui.addACreditCard", "Add a Credit Card")}
            </option>
          )}
          {!noCreate && settings.AchEnabled && (
            <option value="addBank">
              {t("ui.addABankAccount", "Add a Bank Account")}
            </option>
          )}
        </LabeledSelect>
      </div>
      {selectedPaymentMethodId &&
        methods?.find((x) => x.Id == selectedPaymentMethodId) && (
          <WalletBlock
            item={methods.find((x) => x.Id == selectedPaymentMethodId)!}
          />
        )}
      <ExtensionPoint
        name="_site.dashboard.invoices.AchPaymentMessage"
        selectedPaymentMethod={selectedPaymentMethod}
      />
    </div>
  );
};
